/* eslint-disable react-hooks/rules-of-hooks */
import { DndContext, DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ReactFC } from '@formily/reactive-react';
import type { IAny } from '@yimoka/shared';
import React, { createContext, useContext, useMemo } from 'react';

export interface ISortableContainerProps {
  direction?: 'horizontal' | 'vertical'
  list: IAny[]
  start?: number
  accessibility?: {
    container?: Element
  }
  onSortStart?: (event: DragStartEvent) => void
  onSortEnd?: (event: { oldIndex: number; newIndex: number }) => void
}

/** 为任意列表容器接入指定方向的拖拽排序上下文。 */
export function SortableContainer<T extends React.HTMLAttributes<HTMLElement>>(Component: ReactFC<T>): ReactFC<ISortableContainerProps & T> {
  return ({
    direction = 'vertical',
    list,
    start = 0,
    accessibility,
    onSortStart,
    onSortEnd,
    ...props
  }) => {
    /** 把拖拽库的节点标识转换为调用方使用的零基数组下标。 */
    const handleSortEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      const oldIndex = +active.id - 1;
      const newIndex = +over.id - 1;
      onSortEnd?.({
        oldIndex,
        newIndex,
      });
    };

    return (
      <DndContext
        accessibility={accessibility}
        onDragEnd={handleSortEnd}
        onDragStart={onSortStart}
      >
        <SortableContext
          items={list.map((_, index) => index + start + 1)}
          strategy={direction === 'horizontal'
            ? horizontalListSortingStrategy
            : verticalListSortingStrategy}
        >
          <Component {...(props as unknown as T)}>{props.children}</Component>
        </SortableContext>
      </DndContext>
    );
  };
}

export const useSortableItem = () => useContext(SortableItemContext);

export const SortableItemContext = createContext<
  Partial<ReturnType<typeof useSortable>>
>({});

export interface ISortableElementProps {
  index?: number
  lockAxis?: 'x' | 'y'
}

export function SortableElement<T extends React.HTMLAttributes<HTMLElement>>(Component: ReactFC<T>): ReactFC<T & ISortableElementProps> {
  return ({ index = 0, lockAxis, ...props }) => {
    const sortable = useSortable({
      id: index + 1,
    });
    const { setNodeRef, transform, transition, isDragging } = sortable;
    if (transform) {
      switch (lockAxis) {
        case 'x':
          transform.y = 0;
          break;
        case 'y':
          transform.x = 0;
          break;

        default:
          break;
      }
    }

    const style = useMemo(() => {
      const zIndex = transform ? 1 : 'none';
      const position = transform ? 'relative' : 'unset';
      const itemStyle: React.CSSProperties = {
        position,
        touchAction: 'none',
        zIndex,
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : 'none',
        transition: `${transform ? 'all 200ms ease' : ''}`,
      };
      const dragStyle = {
        transition,
        opacity: '0.8',
        transform: `translate3d(${transform?.x || 0}px, ${
          transform?.y || 0
        }px, 0)`,
      };

      const computedStyle = isDragging
        ? {
          ...itemStyle,
          ...dragStyle,
          ...props.style,
        }
        : {
          ...itemStyle,
          ...props.style,
        };

      return computedStyle;
    }, [isDragging, transform, transition, props.style]);

    return (
      <SortableItemContext.Provider value={sortable}>
        {Component({
          ...props,
          style,
          ref: setNodeRef,
        } as unknown as T) as React.ReactNode}
      </SortableItemContext.Provider>
    );
  };
}

export function SortableHandle<T extends React.HTMLAttributes<HTMLElement>>(Component: ReactFC<T>): ReactFC<T> {
  return (props: T) => {
    const { attributes, listeners } = useSortableItem();
    return <Component {...props} {...attributes} {...listeners} />;
  };
}
